import React, { useState, useEffect, useCallback, useRef } from "react";
import { Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import AssetPositionsV2 from "./components/AssetPositionsV2";
import { usePortfolioTableState } from "../../hooks/useOptimizedState";
import { usePortfolioCalculations } from "../Portfolio/utils/usePortfolioCalculations";
import { usePortfolioFilters } from "../Portfolio/usePortfolioFilters";
import { resolveFundAbbreviation, fundAbbreviationsMatch } from "../Portfolio/utils/portfolioFundUtils";
import { usePortfolioUpdates } from "../Portfolio/usePortfolioUpdates";
import { processData } from "../../component/Methods";
import ControlBar from "./components/ControlBar";
import TopMoversDialogV2 from "./components/TopMoversDialogV2";

const PORTFOLIO_V2_DEFAULT_FUND = "WSF";

const syncPortfolioV2Path = (navigate, fundVal, strategyVal) => {
  if (!fundVal || fundVal === "ALL") {
    navigate("/portfolio-v2", { replace: true });
    return;
  }
  if (strategyVal && strategyVal !== "ALL") {
    navigate(
      `/portfolio-v2/${encodeURIComponent(fundVal)}/${encodeURIComponent(strategyVal)}`,
      { replace: true }
    );
    return;
  }
  navigate(`/portfolio-v2/${encodeURIComponent(fundVal)}`, { replace: true });
};

const PortfolioTableV2 = ({
  fundname,
  strategyname,
  setTotals,
  onExportDataChange,
  onExport,
}) => {
  const navigate = useNavigate();
  const [filteredValues, setFilteredValues] = useState([]);
  const [topMoversOpen, setTopMoversOpen] = useState(false);
  const defaultFundSyncedRef = useRef(false);

  const {
    funds,
    lots,
    accountingData,
    signalsPerformance,
    markets,
    livePrices,
    isStrategist,
    managers,
    strategies,
    trades,
  } = usePortfolioTableState();

  const { fund, calculatePositions } = usePortfolioCalculations({
    funds,
    lots,
    accountingData,
    strategies,
    livePrices,
    trades,
    markets,
    isStrategist,
    managers,
    signalsPerformance,
  });

  const {
    fundVal,
    StrategeyVal,
    StrategeyTypename,
    fundOptions,
    strategyOptions,
    StrategyType,
    disableStrategyButton,
    StrategyName,
    setFundFilter,
    setStrategyVal,
    setStrategyTypename,
    setStrategyType,
  } = usePortfolioFilters({
    funds,
    strategies,
    isStrategist,
    fundname,
    strategyname,
    fund,
    defaultFund: PORTFOLIO_V2_DEFAULT_FUND,
  });

  useEffect(() => {
    if (defaultFundSyncedRef.current || isStrategist || fundname) return;
    if (!funds?.length) return;

    const resolved = resolveFundAbbreviation(funds, PORTFOLIO_V2_DEFAULT_FUND);
    if (!funds.some((f) => f.abbreviation === resolved)) return;
    if (!fundAbbreviationsMatch(fundVal, resolved)) return;

    defaultFundSyncedRef.current = true;
    syncPortfolioV2Path(navigate, resolved, "");
  }, [fundVal, fundname, isStrategist, navigate, funds]);

  usePortfolioUpdates({
    calculatePositions,
    fundVal,
    StrategeyVal,
    StrategeyTypename,
    setFilteredValues,
    setTotals,
  });

  useEffect(() => {
    let matchingStrategies;

    if (
      StrategeyVal !== undefined &&
      StrategeyVal !== "ALL" &&
      StrategeyVal !== ""
    ) {
      if (StrategeyTypename === "Both") {
        matchingStrategies = strategies?.find(
          (strategy) => strategy.name === StrategeyVal
        );
      } else if (
        StrategeyTypename === "Long" ||
        StrategeyTypename === "Short"
      ) {
        matchingStrategies = strategies?.find((strategy) =>
          strategy.name.includes(`${StrategeyVal}_${StrategeyTypename}`)
        );
      }
    }

    if (matchingStrategies && matchingStrategies.strategyId) {
      processData(strategies, matchingStrategies.strategyId);
    }
  }, [StrategeyVal, StrategeyTypename, strategies]);

  const handleFundChange = useCallback(
    (e, newValue) => {
      const resolved = resolveFundAbbreviation(funds, newValue);
      setFundFilter(newValue);
      syncPortfolioV2Path(navigate, resolved, "");
      calculatePositions(
        resolved,
        StrategeyVal,
        StrategeyTypename,
        setFilteredValues,
        setTotals
      );
    },
    [
      setFundFilter,
      navigate,
      funds,
      calculatePositions,
      StrategeyVal,
      StrategeyTypename,
      setFilteredValues,
      setTotals,
    ]
  );

  const handleStrategyChange = useCallback(
    (e, newValue) => {
      calculatePositions(
        fundVal,
        newValue,
        StrategeyTypename,
        setFilteredValues,
        setTotals
      );
      setStrategyTypename("Both");
      if (newValue === "ALL") {
        setStrategyType(["Both"]);
        setStrategyVal("ALL");
        syncPortfolioV2Path(navigate, fundVal, "");
      } else {
        setStrategyVal(newValue);
        syncPortfolioV2Path(navigate, fundVal, newValue);
        const selectedStrategy = StrategyName?.find(
          (strategy) => strategy.name === newValue
        );
        if (selectedStrategy && selectedStrategy.checkType === "noLS") {
          setStrategyType(["Both"]);
        } else {
          isStrategist
            ? setStrategyType(["Both"])
            : setStrategyType(["Both", "Long", "Short"]);
        }
      }
    },
    [
      calculatePositions,
      fundVal,
      StrategeyTypename,
      StrategyName,
      isStrategist,
      navigate,
      setStrategyTypename,
      setStrategyType,
      setStrategyVal,
      setFilteredValues,
      setTotals,
    ]
  );

  const handleSideChange = useCallback(
    (e, newValue) => {
      calculatePositions(
        fundVal,
        StrategeyVal,
        newValue,
        setFilteredValues,
        setTotals
      );
      setStrategyTypename(newValue);
    },
    [
      calculatePositions,
      fundVal,
      StrategeyVal,
      setStrategyTypename,
      setFilteredValues,
      setTotals,
    ]
  );

  const handlePositionsExportData = useCallback(
    ({ long, short }) => {
      onExportDataChange?.({
        fundVal,
        StrategeyVal,
        StrategeyTypename,
        longPositions: long,
        shortPositions: short,
      });
    },
    [onExportDataChange, fundVal, StrategeyVal, StrategeyTypename]
  );

  useEffect(() => {
    onExportDataChange?.({
      fundVal,
      StrategeyVal,
      StrategeyTypename,
      longPositions: [],
      shortPositions: [],
    });
  }, [fundVal, StrategeyVal, StrategeyTypename, onExportDataChange]);

  useEffect(() => {
    if (funds && strategies) {
      calculatePositions(
        fundVal,
        StrategeyVal,
        StrategeyTypename,
        setFilteredValues,
        setTotals
      );
    }
  }, [
    funds,
    strategies,
    calculatePositions,
    fundVal,
    StrategeyVal,
    StrategeyTypename,
    setFilteredValues,
    setTotals,
  ]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <ControlBar
        fundVal={fundVal}
        StrategeyVal={StrategeyVal}
        StrategeyTypename={StrategeyTypename}
        fundOptions={fundOptions}
        strategyOptions={strategyOptions}
        StrategyType={StrategyType}
        funds={funds}
        isStrategist={isStrategist}
        disableStrategyButton={disableStrategyButton}
        StrategyName={StrategyName}
        handleFundChange={handleFundChange}
        handleStrategyChange={handleStrategyChange}
        handleSideChange={handleSideChange}
        onExport={onExport}
        onTopMovers={() => setTopMoversOpen(true)}
      />

      <TopMoversDialogV2
        open={topMoversOpen}
        onClose={() => setTopMoversOpen(false)}
        data={filteredValues}
        fundVal={fundVal}
        strategyVal={StrategeyVal}
        side={StrategeyTypename}
      />

      <AssetPositionsV2
        data={filteredValues}
        fundVal={fundVal}
        StrategeyVal={StrategeyVal}
        onExportDataChange={handlePositionsExportData}
      />
    </Box>
  );
};

export default React.memo(PortfolioTableV2);
